import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Transition, animated } from 'react-spring/renderprops.cjs';
import styled from 'styled-components';
import usePopout, { PopoutPlacements } from '../usePopout';
var TooltipContext = /*#__PURE__*/React.createContext();
export function useTooltipState() {
  return useContext(TooltipContext);
}
var AnchorContainer = styled.div.withConfig({
  displayName: "Tooltip__AnchorContainer",
  componentId: "sc-1kcxwf0-0"
})([""]);
var Arrow = styled.div.withConfig({
  displayName: "Tooltip__Arrow",
  componentId: "sc-1kcxwf0-1"
})([""]); // Do not style this BodyContent. Since it is animated, style here will always be loaded at last.

var BodyContent = animated(styled.div.withConfig({
  displayName: "Tooltip__BodyContent",
  componentId: "sc-1kcxwf0-2"
})([""]));
export function TooltipAnchor(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["children"]);

  var context = useTooltipState();
  return /*#__PURE__*/React.createElement(AnchorContainer, _extends({}, context.anchorProps, rest), children);
}
TooltipAnchor.propTypes = {
  children: PropTypes.node
};
export function TooltipArrow(props) {
  var context = useTooltipState();
  return /*#__PURE__*/React.createElement(Arrow, _extends({}, context.arrowProps, props));
}
export function TooltipContent(_ref2) {
  var _ref2$animationDisabl = _ref2.animationDisabled,
      animationDisabled = _ref2$animationDisabl === void 0 ? false : _ref2$animationDisabl,
      children = _ref2.children,
      _ref2$springConfig = _ref2.springConfig,
      springConfig = _ref2$springConfig === void 0 ? {
    tension: 500,
    friction: 40
  } : _ref2$springConfig,
      _ref2$transitionEnter = _ref2.transitionEnter,
      transitionEnter = _ref2$transitionEnter === void 0 ? {
    opacity: 1
  } : _ref2$transitionEnter,
      _ref2$transitionFrom = _ref2.transitionFrom,
      transitionFrom = _ref2$transitionFrom === void 0 ? {
    opacity: 0
  } : _ref2$transitionFrom,
      _ref2$transitionLeave = _ref2.transitionLeave,
      transitionLeave = _ref2$transitionLeave === void 0 ? {
    opacity: 0
  } : _ref2$transitionLeave,
      rest = _objectWithoutProperties(_ref2, ["animationDisabled", "children", "springConfig", "transitionEnter", "transitionFrom", "transitionLeave"]);

  var context = useTooltipState();
  return /*#__PURE__*/React.createElement(Transition, {
    items: context.isShowing,
    from: _objectSpread({}, transitionFrom),
    enter: _objectSpread({}, transitionEnter),
    leave: _objectSpread({}, transitionLeave),
    immediate: animationDisabled,
    config: springConfig,
    unique: true
  }, isVisible => isVisible && (transition => /*#__PURE__*/React.createElement(BodyContent, _extends({
    "aria-hidden": !context.isShowing
  }, context.contentProps, {
    style: _objectSpread(_objectSpread({}, context.contentProps.style), transition)
  }, rest), children)));
}
TooltipContent.propTypes = {
  animationDisabled: PropTypes.bool,
  children: PropTypes.node,
  springConfig: PropTypes.object,
  transitionEnter: PropTypes.object,
  transitionFrom: PropTypes.object,
  transitionLeave: PropTypes.object
};
export default function Tooltip(_ref3) {
  var children = _ref3.children,
      isShowingFromProps = _ref3.isShowing,
      onHide = _ref3.onHide,
      onShow = _ref3.onShow,
      placement = _ref3.placement,
      xOffset = _ref3.xOffset,
      yOffset = _ref3.yOffset;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isShowing = _useState2[0],
      setIsShowing = _useState2[1];

  var _usePopout = usePopout({
    placement,
    xOffset,
    yOffset
  }),
      arrowRef = _usePopout.arrowRef,
      anchorRef = _usePopout.anchorRef,
      popoutRef = _usePopout.popoutRef,
      attributes = _usePopout.attributes,
      styles = _usePopout.styles;

  var showTooltip = useCallback(event => {
    if (isShowingFromProps != null) {
      if (onShow) {
        onShow(event);
      }
    } else {
      setIsShowing(true);
    }
  }, [onShow, isShowingFromProps]);
  var hideTooltip = useCallback(event => {
    if (isShowingFromProps != null) {
      if (onHide) {
        onHide(event);
      }
    } else {
      setIsShowing(false);
    }
  }, [onHide, isShowingFromProps]);
  var toggleTooltip = useCallback(() => {
    if (isShowing) {
      hideTooltip();
    } else {
      showTooltip();
    }
  }, [hideTooltip, isShowing, showTooltip]);
  var anchorProps = {
    onBlur: hideTooltip,
    onFocus: showTooltip,
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onTouchEnd: hideTooltip,
    onTouchStart: showTooltip,
    ref: anchorRef
  };
  var arrowProps = useMemo(() => ({
    ref: arrowRef,
    style: styles.arrow
  }), [arrowRef, styles.arrow]);
  var contentProps = useMemo(() => _objectSpread(_objectSpread({}, attributes.popper), {}, {
    style: styles.popper,
    ref: popoutRef
  }), [attributes.popper, styles.popper, popoutRef]);
  var context = useMemo(() => ({
    anchorProps,
    arrowProps,
    contentProps,
    isShowing,
    showTooltip,
    hideTooltip,
    toggleTooltip
  }), [anchorProps, arrowProps, contentProps, hideTooltip, isShowing, showTooltip, toggleTooltip]);
  return /*#__PURE__*/React.createElement(TooltipContext.Provider, {
    value: context
  }, children);
}
Tooltip.propTypes = {
  children: PropTypes.node,
  isShowing: PropTypes.bool,
  onHide: PropTypes.func,
  onShow: PropTypes.func,
  placement: PropTypes.oneOf(Object.values(PopoutPlacements)),
  xOffset: PropTypes.number,
  yOffset: PropTypes.number
};
Tooltip.Anchor = TooltipAnchor;
Tooltip.Arrow = TooltipArrow;
Tooltip.Content = TooltipContent;
Tooltip.useState = useTooltipState;